/**
 * Reading operations service.
 * Provides async functions for manual reading CRUD operations.
 */

import { queryClient as globalQueryClient } from "common/queryClient";
import { queryKeys } from "common/queryKeys";
import {
  ManualDMInputResponse,
  ReadingMutationResult,
  manualInputApi,
  manualInputMutationApi,
} from "model/readingsApiHelpers";
import { clearTilesFromCache } from "./fieldOperations";

/**
 * Prepare a reading request for the backend.
 * Transforms values to backend format (divides by 10000).
 * Transforms values to backend format.
 */
function prepareReadingRequest(
  manualInput: ManualDMInputResponse
): ManualDMInputResponse {
  return {
    guid: manualInput.guid,
    manualDryMatterInput: {
      ...manualInput.manualDryMatterInput,
      dryMatterValue:
        manualInput.manualDryMatterInput.dryMatterValue &&
        manualInput.manualDryMatterInput.dryMatterValue / 10000,
      dateTimeMeasurementStart:
        manualInput.manualDryMatterInput.dateTimeMeasurementStart,
      dateTimeMeasurementEnd:
        manualInput.manualDryMatterInput.dateTimeMeasurementEnd,
      event: {
        ...manualInput.manualDryMatterInput.event,
        postEventCover:
          manualInput.manualDryMatterInput.event.postEventCover &&
          manualInput.manualDryMatterInput.event.postEventCover / 10000,
        preEventCover:
          manualInput.manualDryMatterInput.event.preEventCover &&
          manualInput.manualDryMatterInput.event.preEventCover / 10000,
      },
    },
  };
}

/**
 * Invalidate readings cache for a field so event lists update.
 */
async function invalidateReadingsCache(fieldGuid: string) {
  await globalQueryClient.invalidateQueries({
    queryKey: queryKeys.events.readings.byField(fieldGuid),
  });
}

async function applyReadingMutationResult(
  result: ReadingMutationResult
): Promise<void> {
  const fieldGuids = (result.affectedFields ?? [])
    .filter((field) => field.tilesInvalidated)
    .map((field) => field.fieldGuid);

  fieldGuids.forEach((fieldGuid) =>
    clearTilesFromCache(fieldGuid, globalQueryClient)
  );

  await Promise.all(
    fieldGuids.map((fieldGuid) =>
      globalQueryClient.invalidateQueries({
        queryKey: queryKeys.fields.detail(fieldGuid),
      })
    )
  );
}

/**
 * Create a single reading.
 * Transforms values to backend format (divides by 10000).
 */
export async function createOneReading(
  reading: ManualDMInputResponse
): Promise<ManualDMInputResponse> {
  const preparedInput = prepareReadingRequest(reading);
  const result = (await manualInputMutationApi.createOne(
    preparedInput
  )) as ReadingMutationResult;
  await applyReadingMutationResult(result);
  await invalidateReadingsCache(reading.manualDryMatterInput.field);
  return manualInputApi.getOne(result.guid ?? "");
}

/**
 * Update a single reading.
 */
export async function updateOneReading(
  reading: ManualDMInputResponse
): Promise<ManualDMInputResponse> {
  const preparedInput = prepareReadingRequest(reading);
  const result = (await manualInputMutationApi.updateOne(
    preparedInput
  )) as ReadingMutationResult;
  await applyReadingMutationResult(result);
  await invalidateReadingsCache(reading.manualDryMatterInput.field);
  return manualInputApi.getOne(reading.guid);
}

/**
 * Delete a single reading.
 */
export async function deleteOneReading(
  readingId: string,
  fieldGuid?: string
): Promise<void> {
  const res = await manualInputMutationApi.deleteOne(readingId);
  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw error;
  }
  const result = (await res.json()) as ReadingMutationResult;
  await applyReadingMutationResult(result);
  if (fieldGuid) {
    await invalidateReadingsCache(fieldGuid);
  }
}
